import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { getClinics, getAdminClinics } from "../../api/clinics";
import { getUser } from "../../store/authStore";
import { setSelectedClinic } from "../../store/selectedClinicStore";
import { getLang } from "../../store/uiStore";

import "./ClinicsPage.css";

export default function ClinicsPage() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  const [nameFilter, setNameFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [reportStatusFilter, setReportStatusFilter] = useState("");

  const navigate = useNavigate();
  const user = getUser();
  const isAdmin = user?.role === "admin";
  const lang = getLang();

  const tr = {
    clinics: lang === "ru" ? "Клиники" : "Муассисаҳо",
    management: lang === "ru" ? "Управление учреждениями" : "Идоракунии муассисаҳо",
    total: lang === "ru" ? "Всего учреждений" : "Ҳамагӣ муассисаҳо",
    found: lang === "ru" ? "Найдено" : "Ёфт шуд",
    name: lang === "ru" ? "Название" : "Ном",
    region: lang === "ru" ? "Область" : "Вилоят",
    district: lang === "ru" ? "Район" : "Ноҳия",
    allRegions: lang === "ru" ? "Все области" : "Ҳамаи вилоятҳо",
    allDistricts: lang === "ru" ? "Все районы" : "Ҳамаи ноҳияҳо",
    reportStatus: lang === "ru" ? "Статус отчёта" : "Ҳолати ҳисобот",
    allReportStatuses: lang === "ru" ? "Все статусы" : "Ҳамаи ҳолатҳо",
    searchPlaceholder: lang === "ru" ? "Поиск по названию..." : "Ҷустуҷӯ аз рӯи ном...",
    reset: lang === "ru" ? "Сбросить фильтры" : "Тоза кардани филтрҳо",
    passport: lang === "ru" ? "Паспорт" : "Шиноснома",
    reports: lang === "ru" ? "Отчёты" : "Ҳисоботҳо",
    noReport: lang === "ru" ? "Нет отчёта" : "Ҳисобот нест",
    loading: lang === "ru" ? "Загрузка..." : "Боргирӣ...",
    actions: lang === "ru" ? "Действия" : "Амалҳо",
    status: lang === "ru" ? "Статус отчёта" : "Ҳолати ҳисобот",
    create: lang === "ru" ? "Создать клинику" : "Муассиса таъсис додан",
    edit: lang === "ru" ? "Редактировать" : "Таҳрир",
    empty: lang === "ru" ? "По заданным параметрам учреждения не найдены" : "Аз рӯи параметрҳои додашуда муассисаҳо ёфт нашуданд",
    statuses: {
      draft: lang === "ru" ? "Черновик" : "Лоиҳа",
      in_progress: lang === "ru" ? "В работе" : "Дар коркард",
      submitted: lang === "ru" ? "Отправлен" : "Фиристода шуд",
      accepted: lang === "ru" ? "Принят" : "Қабул шуд",
      returned: lang === "ru" ? "Возвращён" : "Бозгардонида шуд",
      late: lang === "ru" ? "Просрочен" : "Мӯҳлатгузашта",
      active: lang === "ru" ? "Активна" : "Фаъол",
      suspended: lang === "ru" ? "Приостановлена" : "Боздошта шудааст",
      closed: lang === "ru" ? "Закрыта" : "Баста шудааст",
    },
  };

  const loadClinics = useCallback(async () => {
    try {
      setLoading(true);
      const response = isAdmin ? await getAdminClinics() : await getClinics();
      setClinics(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadClinics();
  }, [loadClinics]);

  const regions = useMemo(() => {
    return uniqueSorted(
      clinics
        .map((clinic) => clinic.passport_region)
        .filter(Boolean)
    );
  }, [clinics]);

  const districts = useMemo(() => {
    return uniqueSorted(
      clinics
        .filter(
          (clinic) =>
            !regionFilter || clinic.passport_region === regionFilter
        )
        .map((clinic) => clinic.passport_district)
        .filter(Boolean)
    );
  }, [clinics, regionFilter]);

  useEffect(() => {
    if (districtFilter && !districts.includes(districtFilter)) {
      setDistrictFilter("");
    }
  }, [districts, districtFilter]);

  const filteredClinics = useMemo(() => {
    const search = nameFilter.trim().toLocaleLowerCase();

    return clinics.filter((clinic) => {
      const name = String(clinic.name || "").toLocaleLowerCase();

      const matchesName = !search || name.includes(search);
      const matchesRegion =
        !regionFilter || clinic.passport_region === regionFilter;
      const matchesDistrict =
        !districtFilter || clinic.passport_district === districtFilter;
      const matchesReportStatus =
        !reportStatusFilter ||
        (reportStatusFilter === "__no_report__"
          ? !clinic.latest_report_status
          : (clinic.latest_report_status || "") === reportStatusFilter);

      return matchesName && matchesRegion && matchesDistrict && matchesReportStatus;
    });
  }, [clinics, nameFilter, regionFilter, districtFilter, reportStatusFilter]);

  const hasFilters =
    nameFilter.trim() !== "" ||
    regionFilter !== "" ||
    districtFilter !== "" ||
    reportStatusFilter !== "";

  const resetFilters = () => {
    setNameFilter("");
    setRegionFilter("");
    setDistrictFilter("");
    setReportStatusFilter("");
  };

  const openPassport = (clinic) => {
    setSelectedClinic(clinic);
    navigate("/passport");
  };

  const openReports = (clinic) => {
    setSelectedClinic(clinic);
    navigate("/reports");
  };

  if (loading) {
    return (
      <main className="nizom-clinics-page">
        <div className="nizom-clinics-loading">{tr.loading}</div>
      </main>
    );
  }

  return (
    <main className="nizom-clinics-page">
      <div className="nizom-clinics-container">
        <div className="nizom-clinics-heading">
          <div>
            <div className="nizom-clinics-eyebrow">{tr.management}</div>
            <h1>{tr.clinics}</h1>
          </div>

          {isAdmin && (
            <button
              type="button"
              className="nizom-clinics-create-button"
              onClick={() => navigate("/clinics/create")}
            >
              <span aria-hidden="true">+</span>
              {tr.create}
            </button>
          )}
        </div>

        <section className="nizom-clinics-card">
          <div className="nizom-clinics-card-header">
            <div>
              <h2>{tr.clinics}</h2>
              <p>
                {tr.total}: {clinics.length}
                {hasFilters && (
                  <>
                    {" · "}
                    {tr.found}: {filteredClinics.length}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="nizom-clinics-filters">
            <div className="nizom-filter-field name-filter">
              <label htmlFor="clinic-name-filter">{tr.name}</label>
              <div className="nizom-filter-search">
                <span aria-hidden="true">⌕</span>
                <input
                  id="clinic-name-filter"
                  type="search"
                  value={nameFilter}
                  onChange={(event) => setNameFilter(event.target.value)}
                  placeholder={tr.searchPlaceholder}
                />
              </div>
            </div>

            <div className="nizom-filter-field">
              <label htmlFor="clinic-region-filter">{tr.region}</label>
              <select
                id="clinic-region-filter"
                value={regionFilter}
                onChange={(event) => setRegionFilter(event.target.value)}
              >
                <option value="">{tr.allRegions}</option>
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>

            <div className="nizom-filter-field">
              <label htmlFor="clinic-district-filter">{tr.district}</label>
              <select
                id="clinic-district-filter"
                value={districtFilter}
                onChange={(event) => setDistrictFilter(event.target.value)}
                disabled={districts.length === 0}
              >
                <option value="">{tr.allDistricts}</option>
                {districts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            <div className="nizom-filter-field">
              <label htmlFor="clinic-report-status-filter">{tr.reportStatus}</label>
              <select
                id="clinic-report-status-filter"
                value={reportStatusFilter}
                onChange={(event) => setReportStatusFilter(event.target.value)}
              >
                <option value="">{tr.allReportStatuses}</option>
                {Object.entries(tr.statuses)
                  .filter(([status]) =>
                    ["draft", "in_progress", "submitted", "accepted", "returned", "late"].includes(status)
                  )
                  .map(([status, label]) => (
                    <option key={status} value={status}>
                      {label}
                    </option>
                  ))}
                <option value="__no_report__">
                  {tr.noReport}
                </option>
              </select>
            </div>

            {hasFilters && (
              <button
                type="button"
                className="nizom-filter-reset"
                onClick={resetFilters}
              >
                × {tr.reset}
              </button>
            )}
          </div>

          <div className="nizom-clinics-table-wrap">
            <table className="nizom-clinics-table">
              <thead>
                <tr>
                  <th>{tr.name}</th>
                  <th>{tr.region}</th>
                  <th>{tr.district}</th>
                  <th>{tr.status}</th>
                  <th>{tr.actions}</th>
                </tr>
              </thead>

              <tbody>
                {filteredClinics.map((clinic) => (
                  <tr key={clinic.id}>
                    <td className="nizom-clinic-name-cell">{clinic.name}</td>
                    <td>{clinic.passport_region || "-"}</td>
                    <td>{clinic.passport_district || "-"}</td>
                    <td>
                      <StatusBadge
                        status={clinic.latest_report_status || "unknown"}
                        label={
                          clinic.latest_report_status
                            ? tr.statuses[clinic.latest_report_status] || clinic.latest_report_status
                            : tr.noReport
                        }
                      />
                    </td>
                    <td>
                      <ClinicActions
                        clinic={clinic}
                        isAdmin={isAdmin}
                        tr={tr}
                        navigate={navigate}
                        openPassport={openPassport}
                        openReports={openReports}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="nizom-clinics-mobile-list">
            {filteredClinics.map((clinic) => (
              <article className="nizom-clinic-mobile-card" key={clinic.id}>
                <div className="nizom-clinic-mobile-header">
                  <h3>{clinic.name}</h3>
                  <StatusBadge
                    status={clinic.latest_report_status || "unknown"}
                    label={
                      clinic.latest_report_status
                        ? tr.statuses[clinic.latest_report_status] || clinic.latest_report_status
                        : tr.noReport
                    }
                  />
                </div>

                <div className="nizom-clinic-mobile-details">
                  <div>
                    <span>{tr.region}</span>
                    <strong>{clinic.passport_region || "-"}</strong>
                  </div>
                  <div>
                    <span>{tr.district}</span>
                    <strong>{clinic.passport_district || "-"}</strong>
                  </div>
                </div>

                <ClinicActions
                  clinic={clinic}
                  isAdmin={isAdmin}
                  tr={tr}
                  navigate={navigate}
                  openPassport={openPassport}
                  openReports={openReports}
                  mobile
                />
              </article>
            ))}
          </div>

          {filteredClinics.length === 0 && (
            <div className="nizom-clinics-empty">
              <div className="nizom-clinics-empty-icon">⌕</div>
              <div>{tr.empty}</div>
              {hasFilters && (
                <button type="button" onClick={resetFilters}>
                  {tr.reset}
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function uniqueSorted(values) {
  return [...new Set(values)].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
  );
}

function StatusBadge({ status, label }) {
  return (
    <span className={`nizom-status-badge status-${status || "unknown"}`}>
      <span className="nizom-status-dot" aria-hidden="true" />
      {label}
    </span>
  );
}

function ClinicActions({
  clinic,
  isAdmin,
  tr,
  navigate,
  openPassport,
  openReports,
  mobile = false,
}) {
  const hasReport = [
    "submitted",
    "returned",
    "accepted",
    "late",
  ].includes(clinic.latest_report_status);

  return (
    <div className={`nizom-clinic-actions ${mobile ? "mobile-actions" : ""}`}>
      {isAdmin && (
        <button
          type="button"
          className="clinic-action secondary"
          onClick={() => navigate(`/clinics/${clinic.id}/edit`)}
        >
          {tr.edit}
        </button>
      )}

      <button
        type="button"
        className="clinic-action primary"
        onClick={() => openPassport(clinic)}
      >
        {tr.passport}
      </button>

      {hasReport ? (
        <button
          type="button"
          className="clinic-action secondary"
          onClick={() => openReports(clinic)}
        >
          {tr.reports}
        </button>
      ) : (
        <button type="button" className="clinic-action disabled" disabled>
          {tr.noReport}
        </button>
      )}
    </div>
  );
}
